import React from 'react';

import { logger } from '../../utils/logger';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * App-level error boundary for the route tree (RW-react-core-standards
 * `coding-standards.md` §11: wrap route boundaries so a render throw shows a
 * recoverable fallback instead of a blank screen).
 *
 * This is a class component on purpose. React error boundaries rely on
 * `getDerivedStateFromError` / `componentDidCatch`, which have no hooks
 * equivalent — the one sanctioned exception to the function-components-only
 * rule (Non-Negotiable #2). Fallback copy stays as plain strings because the
 * i18n layer itself may be the thing that just crashed.
 */
class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    logger.error(
      '[ErrorBoundary] Unhandled render error:',
      error,
      info.componentStack,
    );
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  override render(): React.ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center"
      >
        <h1 className="text-lg font-hk-semibold text-[var(--color-text-primary)]">
          Something went wrong
        </h1>
        <p className="text-sm text-[var(--color-text-secondary)]">
          An unexpected error occurred. Reloading usually fixes it.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="mt-2 rounded border border-solid border-[var(--color-info)] px-4 py-2 text-sm text-[var(--color-info)] cursor-pointer hover:bg-[var(--color-info)] hover:text-white"
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
